import { useMemo } from "react"
import type { DegradedBanner } from "../types"

type DegradedNoticeProps = {
  banner?: DegradedBanner | null
  persona: "demon" | "fairy"
  align?: "full" | "inline"
}

export function DegradedNotice({ banner, persona, align = "full" }: DegradedNoticeProps) {
  const rails = useMemo(() => {
    if (!banner) return []
    if (banner.rails && banner.rails.length) {
      return banner.rails
    }
    if (banner.details && banner.details.length) {
      return banner.details.map((detail) => detail.label).filter(Boolean)
    }
    return []
  }, [banner])

  const detailMessages = useMemo(() => {
    if (!banner?.details) return []
    return banner.details
      .map((detail) => detail.message)
      .filter((message): message is string => Boolean(message && message.trim()))
  }, [banner])

  if (!banner || banner.state !== "degraded") {
    return null
  }

  const primaryCopy = banner.persona_copy?.[persona] ?? banner.default_copy ?? banner.message ?? "Platform degraded — automated fallbacks engaged."
  const showSettingsLink = primaryCopy.toLowerCase().includes("notifications") || (banner.message ?? "").toLowerCase().includes("notifications")

  return (
    <div
      role="status"
      aria-live="polite"
      className="mono small"
      style={{
        border: "1px solid rgba(255,43,79,0.45)",
        padding: align === "inline" ? "8px 12px" : "12px 14px",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        background: "rgba(255,43,79,0.08)",
        color: "var(--error-text)",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <span>{primaryCopy}</span>
      {rails.length ? (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {rails.map((rail) => (
            <span
              key={rail}
              className="tiny mono"
              style={{
                padding: "2px 8px",
                border: "1px solid rgba(255,43,79,0.45)",
                background: "rgba(255,43,79,0.16)",
                letterSpacing: "0.08em",
              }}
            >
              {rail.toUpperCase()}
            </span>
          ))}
        </div>
      ) : null}
      {detailMessages.length ? (
        <ul className="tiny mono" style={{ margin: 0, paddingLeft: "18px", textTransform: "none", color: "var(--error-text)" }}>
          {detailMessages.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      ) : null}
      {showSettingsLink ? (
        <a
          href="/app/settings#notifications"
          className="tiny mono"
          style={{
            alignSelf: "flex-start",
            padding: "4px 8px",
            border: "1px solid rgba(255,43,79,0.6)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "var(--error-text)",
          }}
        >
          Open notifications
        </a>
      ) : null}
    </div>
  )
}

export default DegradedNotice
